import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Tag, Spin, Empty, Space } from 'antd';
import { AndroidOutlined, AppleOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { etatPlateforme, formaterTaille } from './etatPublication';
import { can } from '../../variables/constants';
import { HAB } from '../../variables/habilitations';

const VERT = '#1a6b3c';

export default function TelechargerApplication() {
  const { t } = useTranslation('applicationMobile');
  const [liste, setListe] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await axiosInstance.get('/v1/application-mobile');
      setListe(r.data?.data || []);
    } catch {
      toast.error(t('erreurChargement'));
      setListe([]);
    }
    setChargement(false);
  }, [t]);

  useEffect(() => { charger(); }, [charger]);

  const android = etatPlateforme(liste, 'ANDROID');
  const ios = etatPlateforme(liste, 'IOS');

  /* Le telechargement passe par axios plutot que par un lien direct : la route est
     protegee par un jeton porte par l'intercepteur, qu'une navigation ne joindrait pas. */
  const telechargerApk = async () => {
    setEnCours(true);
    try {
      const r = await axiosInstance.get('/v1/application-mobile/android/fichier',
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = android.nomFichier || 'cbconnect.apk';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const texte = e?.response?.data instanceof Blob ? await e.response.data.text() : null;
      let msg = null;
      try { msg = texte ? JSON.parse(texte)?.message : null; } catch { msg = null; }
      toast.error(msg || t('telechargementImpossible'));
    }
    setEnCours(false);
  };

  const ouvrirAppStore = () => {
    // Le depart vers l'App Store est journalise avant la redirection : au-dela,
    // le serveur ne voit plus rien de ce que fait l'utilisateur.
    axiosInstance.post('/v1/application-mobile/ios/clic', {}, { skipErrorToast: true })
      .catch(() => {});
    window.open(ios.urlExterne, '_blank', 'noopener,noreferrer');
  };

  const Carte = ({ icone, titre, etat, children }) => (
    <Card
      className="cb-card"
      title={<Space>{icone}<span>{titre}</span></Space>}
      style={{ flex: 1, minWidth: 320 }}
    >
      {!etat.publie ? (
        <Empty description={t('aucuneVersion')}
               image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color="green">{t('version', { version: etat.version })}</Tag>
            {etat.datePublication && (
              <Tag>{t('publieeLe', { date: dayjs(etat.datePublication).format('DD/MM/YYYY') })}</Tag>
            )}
          </Space>
          {etat.notes && (
            <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 14, whiteSpace: 'pre-wrap' }}>
              {etat.notes}
            </div>
          )}
          {children}
        </>
      )}
    </Card>
  );

  if (!can(HAB.APP_MOBILE_TELECHARGER)) {
    return (
      <Layout title={t('titre')}>
        <Empty description={t('habilitationRequise')} />
      </Layout>
    );
  }

  return (
    <Layout title={t('titre')}>
      {chargement ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
            <Carte icone={<AndroidOutlined style={{ color: VERT }} />} titre="Android" etat={android}>
              <Button type="primary" icon={<DownloadOutlined />} loading={enCours}
                style={{ background: VERT, borderColor: VERT }} onClick={telechargerApk}>
                {t('telechargerApk', { taille: formaterTaille(android.tailleOctets) })}
              </Button>
            </Carte>

            <Carte icone={<AppleOutlined />} titre="iOS" etat={ios}>
              <Button icon={<AppleOutlined />} onClick={ouvrirAppStore}>
                {t('ouvrirAppStore')}
              </Button>
            </Carte>
          </div>
          <Button icon={<ReloadOutlined />} onClick={charger}>{t('rafraichir')}</Button>
        </>
      )}
    </Layout>
  );
}
